import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
    Fabric,
    IColumn,
    SearchBox,
    DetailsList,
    SelectionMode,
    Selection,
    IDetailsHeaderProps,
    DetailsListLayoutMode,
    ConstrainMode,
    TooltipHost,
    DirectionalHint,
    StickyPositionType,
    DetailsHeader,
    Sticky,
    ScrollbarVisibility,
    ScrollablePane,
    PrimaryButton,
    DefaultButton
} from "@fluentui/react";
import { FiPlus, FiTrash2, FiSave } from "react-icons/fi";
import { toast } from "react-toastify";
import { getDateToDisplay } from "../../util_glob";
import { dpaSearchStyles, orderHeaderCustomStyles } from "../../styles_glob";
import { gloggedinuser } from "../../interfaces";
import { qSaveLinkedDataEdits } from "../../api/qpi";
import styles from "./JsonListTableEditable_dash.module.css";

{/*
    JsonListTableEditable_dash
    Editable twin of JsonListTable_dash used ONLY when `allow_linked_data_editing` is on.
    - Inline cell editing, add row, delete selected rows, explicit Save.
    - Horizontal paste spread: pasting a ';'-delimited string into a cell fills that cell
      and the following columns in the same row, left-to-right.
    - Row selection is used to pick delete targets and the paste target row.
    - Persists via /po_track_v1_edit which upserts po_line_items then rebuilds invoicedata.
    Kept separate so the view-only table stays untouched.
*/}

interface Props {
    data?: string | any[] | Record<string, any>;
    title?: string;
    emptyText?: string;
    fid: string;
    gid: string;
    loggedInUser: gloggedinuser;
    ext_stamp?: Date;
    onPersisted?: (rows: any[]) => void;   // let the parent refresh its cached copy
}

type Row = Record<string, any> & { __k: string };

// columns managed by the backend / identity — hidden from the editable grid but kept on the row.
const SYSTEM_COLS_dash = new Set(["id", "container_name", "space_id", "user_id"]);

let localKeySeq_dash = 0;
const nextLocalKey_dash = () => `__row_${Date.now()}_${localKeySeq_dash++}`;

const prettifyHeader_dash = (key: string): string =>
    key.replace(/[_\-]+/g, " ").replace(/([a-z])([A-Z])/g, "$1 $2").replace(/\b\w/g, c => c.toUpperCase()).trim();

const stringifyCell_dash = (val: any): string => {
    if (val === null || val === undefined) return "";
    if (typeof val === "object") {
        try { return JSON.stringify(val); } catch { return String(val); }
    }
    return String(val);
};

const entryToRow_dash = (r: any): Record<string, any> =>
    r !== null && typeof r === "object" && !Array.isArray(r) ? { ...r } : { value: r };

const normalizeToRows_dash = (data: Props["data"]): Record<string, any>[] => {
    if (data === null || data === undefined) return [];
    let parsed: any = data;
    if (typeof data === "string") {
        const trimmed = data.trim();
        if (trimmed === "") return [];
        try { parsed = JSON.parse(trimmed); } catch { return []; }
    }
    if (Array.isArray(parsed)) return parsed.filter(r => r !== null && r !== undefined).map(entryToRow_dash);
    if (typeof parsed === "object") {
        const arrayProps = Object.values(parsed).filter(v => Array.isArray(v)) as any[][];
        if (arrayProps.length === 1) return arrayProps[0].filter(r => r !== null && r !== undefined).map(entryToRow_dash);
        return [{ ...parsed }];
    }
    return [{ value: parsed }];
};

// union of keys across all rows, preserving first-seen order.
const deriveKeys_dash = (rows: Record<string, any>[]): string[] => {
    const seen = new Set<string>();
    const keys: string[] = [];
    for (const row of rows) {
        if (row && typeof row === "object" && !Array.isArray(row)) {
            for (const k of Object.keys(row)) {
                if (k === "__k") continue;
                if (!seen.has(k)) { seen.add(k); keys.push(k); }
            }
        }
    }
    return keys;
};

const onRenderDetailsHeader_dash = (props?: IDetailsHeaderProps) => (
    <Sticky stickyPosition={StickyPositionType.Header} isScrollSynced={true}>
        <DetailsHeader {...(props as IDetailsHeaderProps)} styles={orderHeaderCustomStyles} />
    </Sticky>
);

const scrollablePaneFillStyles_dash = { root: { position: "absolute" as const, inset: 0, overflowY: "auto" as const } };

const JsonListTableEditable_dash: React.FC<Props> = ({ data, title, emptyText = "No data to display", fid, gid, loggedInUser, ext_stamp = new Date(), onPersisted }) => {
    const [rows, setRows]             = useState<Row[]>([]);
    const [searchText, setSearchText] = useState<string>("");
    const [isSaving, setIsSaving]     = useState<boolean>(false);
    const dirtyKeys                   = useRef<Set<string>>(new Set());
    const deletedIds                  = useRef<(string | number)[]>([]);
    const [, forceTick]               = useState(0);
    const bump = () => forceTick(t => t + 1);

    // (re)seed editable state whenever the source data changes (initial load or parent refresh)
    const seedRows = useCallback((src: Props["data"]) => {
        const seeded = normalizeToRows_dash(src).map(r => ({ ...r, __k: nextLocalKey_dash() })) as Row[];
        setRows(seeded);
        dirtyKeys.current = new Set();
        deletedIds.current = [];
    }, []);

    useEffect(() => { seedRows(data); }, [data, seedRows]);

    // display columns = union of keys minus system columns (system values stay on the row objects)
    const displayKeys = useMemo(() => deriveKeys_dash(rows).filter(k => !SYSTEM_COLS_dash.has(k)), [rows]);

    const selection = useMemo(
        () => new Selection({ getKey: (item: any) => item.__k, onSelectionChanged: () => bump() }),
        []
    );

    const markDirty = (k: string) => { dirtyKeys.current.add(k); };

    const updateCell = useCallback((rowKey: string, col: string, value: string) => {
        setRows(prev => prev.map(r => (r.__k === rowKey ? { ...r, [col]: value } : r)));
        markDirty(rowKey);
    }, []);

    // paste a ';'-delimited string ⇒ fill this cell and the following columns in the same row
    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>, rowKey: string, colIdx: number) => {
        const text = e.clipboardData.getData("text");
        if (!text || text.indexOf(";") === -1) return;    // normal single-cell paste
        e.preventDefault();
        const parts = text.split(";").map(p => p.trim());
        setRows(prev => prev.map(r => {
            if (r.__k !== rowKey) return r;
            const nr = { ...r };
            for (let i = 0; i < parts.length && colIdx + i < displayKeys.length; i++) {
                nr[displayKeys[colIdx + i]] = parts[i];
            }
            return nr;
        }));
        markDirty(rowKey);
    }, [displayKeys]);

    const addRow = useCallback(() => {
        if (displayKeys.length === 0) { toast.info("No columns to add a row to yet"); return; }
        const blank: Row = { __k: nextLocalKey_dash() } as Row;
        displayKeys.forEach(k => { blank[k] = ""; });
        setRows(prev => [blank, ...prev]);
        markDirty(blank.__k);
    }, [displayKeys]);

    const deleteSelected = useCallback(() => {
        const sel = selection.getSelection() as Row[];
        if (sel.length === 0) { toast.info("Select rows to delete"); return; }
        const selKeys = new Set(sel.map(s => s.__k));
        sel.forEach(s => { if (s.id !== undefined && s.id !== null && s.id !== "") deletedIds.current.push(s.id); });
        setRows(prev => prev.filter(r => !selKeys.has(r.__k)));
        selKeys.forEach(k => dirtyKeys.current.delete(k));
        selection.setAllSelected(false);
    }, [selection]);

    const filteredRows = useMemo(() => {
        const term = searchText.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter(row => displayKeys.some(k => stringifyCell_dash(row[k]).toLowerCase().includes(term)));
    }, [rows, displayKeys, searchText]);

    const hasChanges = dirtyKeys.current.size > 0 || deletedIds.current.length > 0;

    const save = useCallback(async () => {
        const upserts = rows
            .filter(r => dirtyKeys.current.has(r.__k))
            .map(({ __k, ...rest }) => rest);
        const deletes = [...deletedIds.current];
        if (upserts.length === 0 && deletes.length === 0) { toast.info("No changes to save"); return; }
        setIsSaving(true);
        try {
            const res = await qSaveLinkedDataEdits(loggedInUser, gid, fid, upserts, deletes);
            if (res.status === "success") {
                toast.success("Linked data saved", { autoClose: 1500 });
                const fresh = res.rows || [];
                seedRows(fresh);
                onPersisted?.(fresh);
            } else {
                toast.error(res.message ? `Save failed: ${res.message}` : "Failed to save linked data");
            }
        } catch {
            toast.error("Failed to save linked data");
        } finally {
            setIsSaving(false);
        }
    }, [rows, gid, fid, loggedInUser, seedRows, onPersisted]);

    const columns: IColumn[] = useMemo(
        () => displayKeys.map((k, colIdx) => ({
            key: k,
            name: prettifyHeader_dash(k),
            fieldName: k,
            minWidth: 90,
            maxWidth: 240,
            isResizable: true,
            isPadded: true,
            onRender: (item: Row) => (
                <input
                    className={styles.cellInput}
                    value={stringifyCell_dash(item[k])}
                    title={stringifyCell_dash(item[k])}
                    onChange={ev => updateCell(item.__k, k, ev.target.value)}
                    onPaste={ev => handlePaste(ev, item.__k, colIdx)}
                />
            )
        })),
        [displayKeys, updateCell, handlePaste]
    );

    const selectedCount = selection.getSelectedCount();

    return (
        <Fabric className={styles.wrapper}>
            <div className={styles.headerBar}>
                <div className={styles.titleAndCount}>
                    <div className={styles.fidBadge}>{fid}</div>
                    {title && <div className={styles.title}>{title}</div>}
                    <TooltipHost content="Number of rows" directionalHint={DirectionalHint.topCenter}>
                        <div className={styles.countBadge}><div className={styles.countLabel}>Count:</div> <span>{filteredRows.length}</span></div>
                    </TooltipHost>
                </div>
                <SearchBox
                    placeholder="Search..."
                    value={searchText}
                    onChange={(_, v) => setSearchText(v || "")}
                    onClear={() => setSearchText("")}
                    styles={dpaSearchStyles}
                />
            </div>

            <div className={styles.toolbar}>
                <DefaultButton className={styles.toolBtn} onClick={addRow} disabled={isSaving}>
                    <FiPlus size={14} /> <span className={styles.toolBtnLabel}>Add row</span>
                </DefaultButton>
                <DefaultButton className={styles.toolBtn} onClick={deleteSelected} disabled={isSaving || selectedCount === 0}>
                    <FiTrash2 size={14} /> <span className={styles.toolBtnLabel}>Delete{selectedCount > 0 ? ` (${selectedCount})` : ""}</span>
                </DefaultButton>
                <div className={styles.toolbarSpacer} />
                {hasChanges && <span className={styles.unsavedTag}>Unsaved changes</span>}
                <PrimaryButton className={styles.saveBtn} onClick={save} disabled={isSaving || !hasChanges}>
                    <FiSave size={14} /> <span className={styles.toolBtnLabel}>{isSaving ? "Saving..." : "Save"}</span>
                </PrimaryButton>
            </div>

            {filteredRows.length > 0 && columns.length > 0 ? (
                <div className={styles.listArea}>
                    <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto} styles={scrollablePaneFillStyles_dash}>
                        <DetailsList
                            className={styles.list}
                            items={filteredRows}
                            columns={columns}
                            selection={selection}
                            selectionMode={SelectionMode.multiple}
                            selectionPreservedOnEmptyClick={true}
                            getKey={(item: Row) => item.__k}
                            layoutMode={DetailsListLayoutMode.justified}
                            constrainMode={ConstrainMode.unconstrained}
                            isHeaderVisible={true}
                            onRenderDetailsHeader={(props?: IDetailsHeaderProps) => onRenderDetailsHeader_dash(props)}
                            setKey="jsonListTableEditableDash"
                            compact={false}
                        />
                    </ScrollablePane>
                </div>
            ) : (
                <div className={styles.emptyState}>{emptyText}</div>
            )}

            <div className={styles.footerWrapper}>
                <div className={styles.dataListModalFooter}><div className={styles.footLabel}>Sync Date:</div>{ext_stamp ? `${getDateToDisplay(ext_stamp)}` : "No Sync Date"}</div>
            </div>
        </Fabric>
    );
};

export default JsonListTableEditable_dash;
